import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { motion } from "motion/react";
import { ApiClientError } from "@victor/api-client";
import { isLoginMfaChallenge } from "@victor/types";
import { useAuth } from "../../auth/AuthContext";
import { Logo } from "../../ui/Logo";

/**
 * Vague 1 · Phase C · Login app agent.
 *
 * Mobile-first :
 *  - Plein écran, padding généreux, inputs grands
 *  - 2 étapes : email/password → (si MFA) code TOTP
 *  - Pas de "mot de passe oublié" embarqué · l'agent qui n'a plus accès va
 *    sur platform.myvictor.fr/reset (le lien email l'y dirige aussi)
 */
export function LoginPage() {
  const { status, login, completeMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // L'AuthGuard pose `state.from` quand il redirige une route privée.
  // Ex. l'agent scanne /scan/<id> sans être loggué → from = "/scan/<id>".
  const returnTo =
    (location.state as { from?: string } | null)?.from ?? "/";
  const [step, setStep] = useState<"creds" | "mfa">("creds");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === "authenticated") return <Navigate to={returnTo} replace />;

  async function handleCreds(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await login({ email, password });
      if (isLoginMfaChallenge(res)) {
        setMfaToken(res.mfaToken);
        setStep("mfa");
      } else {
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.code === "invalid_credentials"
          ? "Email ou mot de passe incorrect."
          : err.message);
      } else {
        setError("Connexion impossible. Vérifiez votre réseau.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMfa(e: FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setError(null);
    setSubmitting(true);
    try {
      await completeMfa(mfaToken, code.replace(/\s/g, ""));
      navigate(returnTo, { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.code === "invalid_mfa_code"
          ? "Code incorrect ou expiré."
          : err.message);
      } else {
        setError("Vérification impossible. Réessayez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto flex max-w-md flex-col items-center"
      >
        <Logo size={64} withWordmark />
        <p className="mt-2 text-sm text-navy-500">App terrain</p>

        <div className="mt-10 w-full rounded-3xl bg-white p-6 shadow-xl shadow-navy-800/5 ring-1 ring-navy-100">
          {step === "creds" ? (
            <form onSubmit={handleCreds} className="space-y-5">
              <h1 className="font-display text-2xl font-black tracking-tight text-navy-800">
                Connexion
              </h1>
              <label className="block">
                <span className="block text-sm font-bold text-navy-700">
                  Email
                </span>
                <input
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-navy-200 bg-white px-4 py-3 text-base focus:border-accent-500 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-bold text-navy-700">
                  Mot de passe
                </span>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-navy-200 bg-white px-4 py-3 text-base focus:border-accent-500 focus:outline-none"
                />
              </label>
              {error ? (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={submitting || !email || !password}
                className="block w-full rounded-xl bg-navy-800 px-4 py-3.5 text-base font-bold text-white transition-colors hover:bg-accent-500 disabled:opacity-50"
              >
                {submitting ? "Connexion…" : "Se connecter"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleMfa} className="space-y-5">
              <div>
                <h1 className="font-display text-2xl font-black tracking-tight text-navy-800">
                  Vérification
                </h1>
                <p className="mt-1 text-sm text-navy-500">
                  Saisissez le code à 6 chiffres de votre app
                  d'authentification.
                </p>
              </div>
              <label className="block">
                <span className="block text-sm font-bold text-navy-700">
                  Code
                </span>
                <input
                  type="text"
                  required
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9 ]{6,8}"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="mt-1 block w-full rounded-xl border border-navy-200 bg-white px-4 py-3 text-center text-2xl tracking-[0.4em] tabular-nums focus:border-accent-500 focus:outline-none"
                />
              </label>
              {error ? (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={submitting || code.replace(/\s/g, "").length < 6}
                className="block w-full rounded-xl bg-navy-800 px-4 py-3.5 text-base font-bold text-white transition-colors hover:bg-accent-500 disabled:opacity-50"
              >
                {submitting ? "Vérification…" : "Valider"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStep("creds");
                  setMfaToken(null);
                  setCode("");
                  setError(null);
                }}
                className="block w-full rounded-xl px-4 py-2 text-sm font-semibold text-navy-600 hover:bg-navy-50"
              >
                ← Retour
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-navy-500">
          Mot de passe oublié ?{" "}
          <a
            href="https://platform.myvictor.fr/login"
            className="font-bold text-accent-600"
          >
            Réinitialiser
          </a>
        </p>
      </motion.div>
    </div>
  );
}
